<script lang="ts">
	// UC-11. The people who can unlock a till: each belongs to one shop and
	// holds one passcode. Adding, changing and resetting a passcode happen in
	// a Sheet; removing is undone, not confirmed.
	//
	// A passcode is typed twice, and the second box takes the cursor the
	// moment the first holds eight digits, so a slip shows before it is
	// saved. It is never shown again.

	import Band from '../components/Band.svelte';
	import Button from '../components/Button.svelte';
	import Card from '../components/Card.svelte';
	import Icon from '../components/Icon.svelte';
	import Field from '../components/Field.svelte';
	import Loading from '../components/Loading.svelte';
	import Row from '../components/Row.svelte';
	import Segmented from '../components/Segmented.svelte';
	import Sheet from '../components/Sheet.svelte';
	import TablePanel from '../components/TablePanel.svelte';
	import { api } from '../api/client';
	import { message, type Language } from '../i18n/messages';
	import { loadCashiers, ownerShops, ownerCashiers, type Cashier } from '../owner/data.svelte';
	import { flash, removeWithUndo } from '../owner/notices.svelte';
	import { tick } from 'svelte';

	interface Props {
		language: Language;
		/** Bumped by the title row's "New cashier". */
		actionAsked: number;
		shopId?: string;
	}

	let { language, actionAsked, shopId = '' }: Props = $props();

	const PASSCODE_LENGTH = 8;
	// UC-11.R12: domain.MaxCashierNameLength, the longest name the till
	// header's one row holds whole. The box keeps the contract's bound, so a
	// longer name pasted in is shown and refused under the field, never cut.
	const NAME_LENGTH = 22;
	const NAME_BOUND = 120;

	const shops = $derived(ownerShops());
	const people = $derived(ownerCashiers());
	const shownPeople = $derived(
		people.value.filter((person) => !shopId || person.shopId === shopId),
	);
	const noShops = $derived(shops.loaded && shops.value.length === 0);

	type Mode = 'new' | 'edit' | 'reset';
	interface Form {
		mode: Mode;
		id: string;
		name: string;
		originalName: string;
		shopId: string;
		originalShop: string;
		role: 'cashier' | 'manager';
		pass: string;
		again: string;
	}

	let form = $state<Form | null>(null);
	let open = $state(false);
	let saving = $state(false);
	let passError = $state('');
	let failure = $state('');

	$effect(() => {
		if (actionAsked > 0 && !noShops) start('new', null);
	});

	function start(mode: Mode, person: Cashier | null) {
		form = {
			mode,
			id: person?.id ?? '',
			name: person?.name ?? '',
			originalName: person?.name ?? '',
			shopId: person?.shopId ?? (shopId || shops.value[0]?.id) ?? '',
			originalShop: person?.shopId ?? '',
			role: person?.role ?? 'cashier',
			pass: '',
			again: '',
		};
		passError = '';
		failure = '';
		open = true;
	}

	const mismatch = $derived(
		!!form &&
			form.pass.length === PASSCODE_LENGTH &&
			form.again.length === PASSCODE_LENGTH &&
			form.pass !== form.again,
	);
	const matched = $derived(
		!!form && form.pass.length === PASSCODE_LENGTH && form.pass === form.again,
	);
	// A new cashier and a reset need a passcode; an edit may leave it alone.
	const passcodeNeeded = $derived(!!form && form.mode !== 'edit');
	const passcodeOk = $derived(
		!!form && (form.pass === '' && !passcodeNeeded ? form.again === '' : matched),
	);
	// A name saved before the limit keeps saving as it is; only a new name,
	// or one the edit changes, is held to it, as the server holds it.
	const nameTooLong = $derived(
		!!form &&
			form.mode !== 'reset' &&
			form.name.trim() !== form.originalName &&
			[...form.name.trim()].length > NAME_LENGTH,
	);
	const ready = $derived(
		!!form &&
			(form.mode === 'reset' || (form.name.trim() !== '' && !nameTooLong && form.shopId !== '')) &&
			passcodeOk,
	);

	async function typePass(value: string) {
		if (!form) return;
		form.pass = value.replace(/\D/g, '').slice(0, PASSCODE_LENGTH);
		passError = '';
		// The first entry moves on by itself at eight digits.
		if (form.pass.length === PASSCODE_LENGTH) {
			await tick();
			document.getElementById('field-cashier-passcode-again')?.focus();
		}
	}

	async function save() {
		if (!form || !ready || saving) return;
		saving = true;
		failure = '';
		passError = '';
		try {
			let error: { code?: string; message?: string } | undefined;
			if (form.mode === 'reset') {
				({ error } = await api.PUT('/api/owner/cashiers/{userId}/passcode', {
					params: { path: { userId: form.id } },
					body: { passcode: form.pass },
				}));
			} else {
				const body = {
					name: form.name.trim(),
					shopId: form.shopId,
					role: form.role,
					...(form.pass ? { passcode: form.pass } : {}),
				};
				({ error } =
					form.mode === 'new'
						? await api.POST('/api/owner/cashiers', { body })
						: await api.PATCH('/api/owner/cashiers/{userId}', {
								params: { path: { userId: form.id } },
								body,
							}));
			}
			if (error !== undefined) {
				if (error.code === 'PASSCODE_IN_USE')
					passError = message(language, 'cashiersPasscodeInUse');
				else failure = error.message ?? message(language, 'loadFailed');
				return;
			}
			const mode = form.mode;
			open = false;
			await loadCashiers(message(language, 'loadFailed'));
			flash(message(language, mode === 'reset' ? 'cashiersPasscodeChanged' : 'saved'));
		} catch {
			failure = message(language, 'loadFailed');
		} finally {
			saving = false;
		}
	}

	function remove(person: Cashier) {
		const at = people.value.indexOf(person);
		people.value = people.value.filter((c) => c.id !== person.id);
		removeWithUndo(
			`${message(language, 'cashiersRemoved')} ${person.name}. ${message(language, 'cashiersSignedOut')}`,
			async () => {
				try {
					await api.POST('/api/owner/cashiers/{userId}/deactivate', {
						params: { path: { userId: person.id } },
					});
				} finally {
					await loadCashiers(message(language, 'loadFailed'));
				}
			},
			() => {
				people.value.splice(at, 0, person);
			},
		);
	}

	function shopName(id: string): string {
		return shops.value.find((b) => b.id === id)?.name ?? '';
	}

	const title = $derived(
		!form
			? ''
			: form.mode === 'new'
				? message(language, 'cashiersAdd')
				: form.mode === 'reset'
					? `${message(language, 'cashiersResetFor')} ${form.name}`
					: `${message(language, 'cashiersEdit')} ${form.name}`,
	);

	const passNote = $derived(
		!form
			? ''
			: form.mode === 'new'
				? message(language, 'cashiersPasscodeHint')
				: form.mode === 'edit'
					? message(language, 'cashiersPasscodeKeep')
					: message(language, 'cashiersPasscodeHint'),
	);
</script>

<div class="cashiers" data-testid="panel-cashiers">
	{#if !people.loaded || !shops.loaded}
		<div class="wait">
			<Loading
				size="lg"
				label={message(language, 'loading')}
				caption={message(language, 'loading')}
			/>
		</div>
	{:else}
		{#if people.failure}
			<Band tone="flag" testId="cashiers-error">{people.failure}</Band>
		{/if}
		{#if noShops}
			<Card pad>
				<p class="empty" data-testid="cashiers-no-shops">{message(language, 'cashiersNoShops')}</p>
			</Card>
		{:else if shownPeople.length === 0}
			<Card pad>
				<p class="empty" data-testid="cashiers-empty">{message(language, 'cashiersEmpty')}</p>
			</Card>
		{:else}
			<TablePanel
				columns={[
					{ label: message(language, 'cashiersName'), w: 'minmax(0,1.2fr)' },
					{ label: message(language, 'cashiersShop'), w: 'minmax(0,1fr)' },
					{ label: message(language, 'cashiersRole'), w: '96px' },
					{ label: '', w: 'minmax(0,1.6fr)' },
				]}
				testId="cashiers-list"
			>
				{#each shownPeople as person (person.id)}
					<Row testId="cashier-{person.id}">
						<span class="name">{person.name}</span>
						<span class="soft">{shopName(person.shopId)}</span>
						<!-- A kind, not a state: plain words, the manager told by weight and
						     the key. A filled chip beside the row's buttons read as one more. -->
						<span
							class="role"
							class:manager={person.role === 'manager'}
							data-testid="cashier-role-{person.id}"
						>
							<Icon name={person.role === 'manager' ? 'key' : 'user'} size={18} />
							{person.role === 'manager'
								? message(language, 'cashiersRoleManager')
								: message(language, 'cashiersRoleCashier')}
						</span>
						<span class="actions">
							<Button
								testId="cashier-edit-{person.id}"
								label={message(language, 'edit')}
								tone="text"
								size="sm"
								onPress={() => start('edit', person)}
							/>
							<Button
								testId="cashier-reset-{person.id}"
								label={message(language, 'cashiersNewPasscode')}
								tone="text"
								size="sm"
								onPress={() => start('reset', person)}
							/>
							<Button
								testId="cashier-remove-{person.id}"
								label={message(language, 'remove')}
								tone="text-quiet"
								size="sm"
								onPress={() => remove(person)}
							/>
						</span>
					</Row>
				{/each}
			</TablePanel>
		{/if}
	{/if}
</div>

<Sheet
	{open}
	{title}
	primaryLabel={form?.mode === 'new'
		? message(language, 'cashiersAddAction')
		: message(language, 'save')}
	primaryTestId="cashier-save"
	primaryDisabled={!ready}
	primaryState={saving ? 'pending' : 'idle'}
	pendingLabel={message(language, 'saving')}
	onPrimary={() => void save()}
	onClose={() => (open = false)}
	closeLabel={message(language, 'sheetClose')}
	closeTestId="cashier-close"
	testId="cashier-form"
>
	{#if form}
		<div class="sheet" data-scroll="">
			{#if failure}
				<Band tone="flag" testId="cashier-error">{failure}</Band>
			{/if}
			<div class="fields">
				{#if form.mode !== 'reset'}
					<Field
						testId="cashier-name"
						label={message(language, 'cashiersName')}
						value={form.name}
						maxLength={NAME_BOUND}
						error={nameTooLong ? message(language, 'cashiersNameTooLong') : undefined}
						errorTestId="cashier-name-error"
						onInput={(v) => form && (form.name = v)}
					/>
					<div class="group">
						<span class="label">{message(language, 'cashiersShop')}</span>
						<div class="picks">
							{#each shops.value as shop (shop.id)}
								<Button
									testId="cashier-shop-{shop.id}"
									label={shop.name}
									tone={form.shopId === shop.id ? 'ink' : 'secondary'}
									size="sm"
									onPress={() => form && (form.shopId = shop.id)}
								/>
							{/each}
						</div>
						{#if form.mode === 'edit' && form.shopId !== form.originalShop}
							<span class="warn">{message(language, 'cashiersMoveWarn')}</span>
						{/if}
					</div>
					<div class="group">
						<span class="label">{message(language, 'cashiersRole')}</span>
						<Segmented
							label={message(language, 'cashiersRole')}
							options={[
								{
									value: 'cashier',
									label: message(language, 'cashiersRoleCashier'),
									testId: 'cashier-role-cashier',
								},
								{
									value: 'manager',
									label: message(language, 'cashiersRoleManager'),
									testId: 'cashier-role-manager',
								},
							]}
							value={form.role}
							onChange={(picked) => form && (form.role = picked as 'cashier' | 'manager')}
						/>
					</div>
				{/if}
				<Field
					testId="cashier-passcode"
					label={message(language, 'cashiersPasscode')}
					value={form.pass}
					note={passNote}
					error={passError || undefined}
					inputMode="numeric"
					maxLength={PASSCODE_LENGTH}
					secret
					mono
					onInput={(v) => void typePass(v)}
				/>
				<Field
					testId="cashier-passcode-again"
					label={message(language, 'cashiersPasscodeAgain')}
					value={form.again}
					note={matched ? message(language, 'cashiersPasscodeMatch') : undefined}
					error={mismatch ? message(language, 'cashiersPasscodeDiffer') : undefined}
					inputMode="numeric"
					maxLength={PASSCODE_LENGTH}
					secret
					mono
					onInput={(v) => form && (form.again = v.replace(/\D/g, '').slice(0, PASSCODE_LENGTH))}
				/>
			</div>
		</div>
	{/if}
</Sheet>

<style>
	.wait {
		display: grid;
		place-items: center;
		min-block-size: 240px;
	}

	.cashiers {
		display: flex;
		flex-direction: column;
		gap: var(--space-9);
	}

	.empty {
		margin: 0;
		font-size: var(--text-md);
		color: var(--text-body);
	}

	.name {
		min-inline-size: 0;
		font-weight: 600;
	}

	.soft {
		min-inline-size: 0;
		color: var(--text-body);
	}

	.role {
		min-inline-size: 0;
		display: inline-flex;
		align-items: center;
		gap: var(--space-3);
		font-size: var(--text-2xs);
		color: var(--text-body);
	}

	.role :global(svg) {
		color: var(--text-muted);
	}

	.role.manager {
		font-weight: 700;
		color: var(--text-strong);
	}

	.role.manager :global(svg) {
		color: currentColor;
	}

	.actions {
		min-inline-size: 0;
		display: flex;
		gap: var(--space-3);
		justify-content: flex-end;
		flex-wrap: wrap;
	}

	.sheet {
		flex: 1;
		min-block-size: 0;
		overflow-y: auto;
		overflow-x: hidden;
		display: flex;
		flex-direction: column;
		gap: var(--space-7);
	}

	.fields {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(min(100%, 240px), 1fr));
		gap: var(--space-7);
	}

	.group {
		display: flex;
		flex-direction: column;
		gap: var(--space-2);
		min-inline-size: 0;
	}

	.label {
		font-size: var(--text-2xs);
		color: var(--text-muted);
	}

	.picks {
		display: flex;
		flex-wrap: wrap;
		gap: var(--space-2);
	}

	.warn {
		font-size: var(--text-2xs);
		font-weight: 600;
		color: var(--ink-flag-deep);
	}
</style>
